/**
 * SendMessageDialog — o seletor de modelo usado em TODO ponto de envio.
 *
 * Fluxo: escolhe o modelo (o `is_default` da categoria vem pré-selecionado) →
 * vê o texto JÁ RENDERIZADO com os dados do documento → pode editar → envia.
 * O texto final é SEMPRE editável: nada sai daqui sem o usuário poder revisar.
 *
 * Garantias que este componente precisa manter:
 *  - ENVIAR É GRÁTIS. Sem PRO (ou sem nenhum modelo cadastrado, ou offline) ele
 *    cai em `defaultBodyFor(category)` e o envio funciona igual. O que é PRO é
 *    criar modelos próprios — e isso é decidido na tela de gerenciamento.
 *  - Renderiza PRIMEIRO, codifica DEPOIS: quem monta a URL é
 *    `buildWhatsAppUrl`/`buildMailtoUrl`, que fazem exatamente um
 *    `encodeURIComponent` sobre a string final (ver messageTemplates.ts).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Mail, Settings2 } from 'lucide-react';
// O logo OFICIAL do WhatsApp, e não um balão genérico do lucide: o botão manda
// para um aplicativo específico, e o ícone é o que o usuário reconhece antes de
// ler o rótulo.
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { useMessageTemplatesByCategory } from '@/hooks/useMessageTemplates';
import {
  buildMailtoUrl,
  buildWhatsAppUrl,
  defaultBodyFor,
  renderTemplate,
  type MessageTemplateCategory,
  type TemplateContext,
} from '@/lib/messageTemplates';

export type SendChannel = 'whatsapp' | 'email';

interface SendMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: MessageTemplateCategory;
  /** Valores crus do documento; a formatação pt-BR acontece no renderer. */
  context: TemplateContext;
  /** Telefone do destinatário (qualquer formato — é normalizado no envio). */
  phone?: string | null;
  /** Destinatário do e-mail, quando o canal 'email' estiver habilitado. */
  email?: string | null;
  /** Assunto do e-mail. Ignorado quando só há o canal WhatsApp. */
  emailSubject?: string;
  /** Canais oferecidos. Padrão: só WhatsApp. */
  channels?: SendChannel[];
  title?: string;
  description?: string;
}

export function SendMessageDialog({
  open,
  onOpenChange,
  category,
  context,
  phone,
  email,
  emailSubject,
  channels = ['whatsapp'],
  title = 'Enviar por WhatsApp',
  description,
}: SendMessageDialogProps) {
  const navigate = useNavigate();
  const { templates, defaultTemplate } = useMessageTemplatesByCategory(category);

  const [selectedId, setSelectedId] = useState('');
  const [text, setText] = useState('');
  // Depois que o usuário edita à mão, nada mais sobrescreve o texto — nem a
  // chegada tardia dos modelos do servidor (a query revalida a cada montagem).
  const [touched, setTouched] = useState(false);

  // O contexto chega como objeto literal (identidade nova a cada render do pai).
  // Guardo o valor num ref e uso uma CHAVE SERIALIZADA como dependência, senão o
  // efeito de renderização re-dispararia a cada render do pai.
  const contextKey = JSON.stringify(context ?? {});
  const contextRef = useRef(context);
  contextRef.current = context;

  const selectedTemplate = useMemo(() => {
    if (selectedId) return templates.find((t) => t.id === selectedId) ?? null;
    return defaultTemplate;
  }, [templates, selectedId, defaultTemplate]);

  // String (não objeto) de propósito: dependência estável para o efeito abaixo.
  const baseBody = selectedTemplate?.body ?? defaultBodyFor(category);

  // Reabrir o diálogo recomeça do modelo padrão — não herda a edição anterior.
  useEffect(() => {
    if (open) {
      setTouched(false);
      setSelectedId('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || touched) return;
    setText(renderTemplate(baseBody, contextRef.current));
  }, [open, touched, baseBody, contextKey]);

  const canSend = text.trim().length > 0;

  const sendWhatsApp = () => {
    window.open(buildWhatsAppUrl(phone, text), '_blank');
    onOpenChange(false);
  };

  const sendEmail = () => {
    window.open(
      buildMailtoUrl({ to: email, subject: emailSubject ?? title, body: text }),
      '_blank',
    );
    onOpenChange(false);
  };

  const showsPicker = templates.length > 0;
  const showsWhatsApp = channels.includes('whatsapp');
  const showsEmail = channels.includes('email');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ?? 'Revise o texto antes de enviar. Você pode editar à vontade.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showsPicker && (
            <div className="space-y-2">
              <Label htmlFor="message-template">Modelo</Label>
              <Select
                value={selectedId || defaultTemplate?.id || ''}
                onValueChange={(value) => {
                  setSelectedId(value);
                  // Trocar de modelo é um pedido explícito de texto novo.
                  setTouched(false);
                }}
              >
                <SelectTrigger id="message-template">
                  <SelectValue placeholder="Escolha um modelo" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="message-body">Mensagem</Label>
            <Textarea
              id="message-body"
              value={text}
              onChange={(e) => {
                setTouched(true);
                setText(e.target.value);
              }}
              rows={7}
              className="resize-none"
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              onOpenChange(false);
              navigate('/modelos-mensagens');
            }}
          >
            <Settings2 className="mr-2 h-3.5 w-3.5" />
            {showsPicker ? 'Gerenciar modelos' : 'Criar modelos de mensagem'}
          </Button>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button3D color="gray" className="flex-1" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          {showsEmail && (
            <Button3D
              color={showsWhatsApp ? 'gray' : 'green'}
              className="flex-1"
              disabled={!canSend}
              onClick={sendEmail}
            >
              <Mail className="mr-2 h-4 w-4" />
              E-mail
            </Button3D>
          )}
          {showsWhatsApp && (
            <Button3D color="whatsapp" className="flex-1" disabled={!canSend} onClick={sendWhatsApp}>
              <WhatsAppIcon className="mr-2 h-4 w-4" />
              WhatsApp
            </Button3D>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
